import React, { ReactElement } from 'react'
import styled from 'styled-components'
import { screenMin, screen } from '@farewill/ui/helpers/responsive'
import { Image, H, P, Wrapper } from '@farewill/ui'
import { COLOR, GTR, FONT } from '@farewill/ui/tokens'
import { CORE_PRODUCTS } from 'lib/products/constants'

const StyledProductCard = styled(Wrapper)`
  display: flex;
  flex-direction: column;

  ${screenMin.m`
    align-items: center;
    flex-direction: row;
  `}
  ${screen.xl`
    display: flex;
   flex-direction: column;
 `}
`

const StyledImageWrapper = styled(Wrapper)`
  max-width: 300px;
  margin: 0px auto;
  ${screenMin.m`
    margin: 0px 0px 0px auto;
  `}
  ${screen.xl`
    margin: 0px auto;
 `}
`

const StyledContentWrapper = styled(Wrapper)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
  padding-top: ${GTR.L};

  ${screenMin.m`
    padding-left: ${GTR.L};
    padding-top:0;
    max-width: 900px;
  `}
  ${screen.xl`
    padding-left: 0;
    padding-top: ${GTR.L};
    max-width: 300px;
 `}
`

const StyledHomeProducts = styled.ul`
  padding-top: ${GTR.M};
  ${screenMin.m`
  padding-top: ${GTR.XL};
`}
  ${screenMin.xl`
    padding-top: 62px;
    display: flex;
    justify-content: center;
    align-items: stretch;
  `}

  & li {
    margin-bottom: ${GTR.M};

    ${screenMin.xl`
      width: ${100 / CORE_PRODUCTS.length}%;
      max-width: 400px;
      margin-bottom: 0;

      &:not(:last-of-type) {
        margin-right: ${GTR.M};
      }
    `}
  }
`

const StyledH = styled(H)`
  text-wrap: balance;
  font-size: 40px;
  ${screenMin.m`
    text-align: center;
  `}
  ${screen.s`
    font-size:${FONT.SIZE.XL}
  `}
`
const StyledText = styled.p`
  font-size: ${FONT.SIZE.L};
  max-width: 960px;
  justify-self: center;
`
const StyledListItem = styled.li`
  margin: 0px;
`

const StyledBr = styled.br`
  display: none;
  ${screenMin.m`
    display: block;
  `}
`
type ImpactProps = {
  className?: string
  title: string
  image?: string
  description?: string
  imageAltText: string
  children: string
}
const Impact = ({
  className,
  title,
  image,
  description,
  imageAltText,
  children,
}: ImpactProps): ReactElement => (
  <StyledProductCard
    className={className}
    container
    padding={['M', 0]}
    paddingFromM={['M', '0']}
    paddingFromL="M"
  >
    <StyledImageWrapper>
      <Image
        path={`com-assets/bespoke-journey-cruk/${image}`}
        ext="jpg"
        widthFromXL={250}
        widthFromM={150}
        width={180}
        alt={imageAltText}
      />
    </StyledImageWrapper>
    <StyledContentWrapper>
      <Wrapper margin={[0, 0, 'M', 0]}>
        <H size="S">{title}</H>
        <P>{description}</P>
      </Wrapper>
      {children}
    </StyledContentWrapper>
  </StyledProductCard>
)

const PartnerBenefit = (): ReactElement => (
  <Wrapper container color={COLOR.BLACK}>
    <Wrapper>
      <StyledH tag="h3" size="XL" decorative>
        Make the most impact with a<StyledBr /> percentage share gift
      </StyledH>
      <StyledText>
        The most valuable type of gift is a percentage share of an estate as
        it’s inflation-proof. Your estate is all your assets combined, and you
        can choose to give a percent of this to Cancer Research UK.
      </StyledText>
    </Wrapper>
    <StyledHomeProducts>
      <StyledListItem>
        <Impact
          image="circle-illustration-1-gift"
          imageAltText="An illustration of a pie chart with a highlighted segment and a family standing beside it."
          title="Take care of your friends and family first"
        >
          After taking care of your loved ones, you can choose to gift a
          percentage of your estate to charity. This percentage can be as small
          or as large as you want and will help to power progress.
        </Impact>
      </StyledListItem>
      <StyledListItem>
        <Impact
          image="circle-illustration-2-inflation"
          imageAltText="An illustration of a tablet showing a rising line graph."
          title="Your gift will be as valuable in the future as it is today"
        >
          Unlike a lump sum gift, a percentage share gift grows in line with the
          value of your estate. So, if the value of your estate increases over
          time, the value of your gift will grow too.
        </Impact>
      </StyledListItem>
      <StyledListItem>
        <Impact
          image="circle-illustration-3-legacy"
          imageAltText="An illustration of a Cancer Research UK support nurse talking with someone and offering support."
          title="Your legacy will make a lasting difference"
        >
          Percentage share gifts can help Cancer Research UK to save and improve
          lives through research, information and influence, long into the
          future.
        </Impact>
      </StyledListItem>
    </StyledHomeProducts>
  </Wrapper>
)

export default PartnerBenefit
